import { UsersPublicApi, WorkspacesApi } from '@up2stay/up2promo-api-client';
import { healthOkFgClass, Input } from '@up2stay/up2promo-ui';
import type { AxiosError } from 'axios';
import { AnimatePresence, motion } from 'motion/react';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useDictionary } from '@/core/providers/dictionary-provider';

/**
 * Converts German umlauts / sharp s and other accented Latin letters to plain
 * ASCII so they survive the username's allowed-char filter. Mirrors the
 * backend's `transliterateUsername` in `@core/utils/generate-username.util`.
 */
function transliterateUsername(input: string): string {
  return input
    .replace(/Ä/g, 'Ae')
    .replace(/Ö/g, 'Oe')
    .replace(/Ü/g, 'Ue')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

interface UsernameInputProps {
  value: string;
  onChange: (value: string) => void;
  firstName?: string;
  lastName?: string;
  /** When set (including empty string), uses workspace username APIs. */
  workspaceName?: string;
  onProposalSelect?: (username: string) => void;
  error?: string;
  disabled?: boolean;
  excludeUserId?: string;
  excludeWorkspaceId?: string;
  /** Overrides the personal “choose a username” placeholder. */
  placeholder?: string;
  /** Input id, so the field's visible `<Label htmlFor>` names it (DEV-540). */
  id?: string;
}

type AvailabilityStatus = 'idle' | 'checking' | 'available' | 'unavailable';

export function UsernameInput({
  value,
  onChange,
  firstName,
  lastName,
  workspaceName,
  onProposalSelect,
  error,
  disabled,
  excludeUserId,
  excludeWorkspaceId,
  placeholder,
  id,
}: UsernameInputProps) {
  const dict = useDictionary();
  const authDict = dict.auth.common;
  const isWorkspaceScope =
    workspaceName !== undefined || excludeWorkspaceId !== undefined;

  const [availabilityStatus, setAvailabilityStatus] =
    useState<AvailabilityStatus>('idle');
  const [proposals, setProposals] = useState<string[]>([]);
  const [showProposals, setShowProposals] = useState(false);
  const [isLoadingProposals, setIsLoadingProposals] = useState(false);
  const isFocusedRef = useRef(false);

  // Refs for abort controllers
  const abortControllerRef = useRef<AbortController | null>(null);
  const proposalsAbortControllerRef = useRef<AbortController | null>(null);

  // Refs for debounce timers
  const valueDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const firstNameDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const lastNameDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced values
  const [debouncedValue, setDebouncedValue] = useState(value);
  const [debouncedFirstName, setDebouncedFirstName] = useState(firstName || '');
  const [debouncedLastName, setDebouncedLastName] = useState(lastName || '');
  const [debouncedWorkspaceName, setDebouncedWorkspaceName] = useState(
    workspaceName || '',
  );

  // Server availability tracking - separate for proposals and availability checks
  const [proposalsServerUnavailable, setProposalsServerUnavailable] =
    useState(false);
  const [availabilityServerUnavailable, setAvailabilityServerUnavailable] =
    useState(false);
  const proposalsServerCooldownRef = useRef<NodeJS.Timeout | null>(null);
  const availabilityServerCooldownRef = useRef<NodeJS.Timeout | null>(null);
  const proposalsConsecutiveFailuresRef = useRef(0);
  const availabilityConsecutiveFailuresRef = useRef(0);
  const proposalsLastFailureTimeRef = useRef<number | null>(null);
  const availabilityLastFailureTimeRef = useRef<number | null>(null);

  // Track last checked username to prevent duplicate checks
  const lastCheckedUsernameRef = useRef<string | null>(null);
  const lastLoadedNamesRef = useRef<{
    first: string;
    last: string;
    workspace?: string;
  } | null>(null);
  const proposalsLoadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [lastLoadedBaseUsername, setLastLoadedBaseUsername] = useState<
    string | null
  >(null);

  // Never treat suggestions as verified — checkmark only after availability API.
  // While the typed value is ahead of the debounce window, hide verified status
  // so a stale checkmark cannot linger on an unverified edit.
  const resolvedAvailabilityStatus: AvailabilityStatus =
    !value || value.length < 3
      ? 'idle'
      : value !== debouncedValue
        ? 'idle'
        : !debouncedValue || debouncedValue.length < 3
          ? 'idle'
          : availabilityStatus;

  // Reset last checked username when value changes (not debounced) to allow re-checking
  useEffect(() => {
    if (value !== debouncedValue) {
      lastCheckedUsernameRef.current = null;
    }
  }, [value, debouncedValue]);

  const [prevDebouncedValue, setPrevDebouncedValue] = useState(debouncedValue);
  if (
    debouncedValue !== prevDebouncedValue &&
    (!debouncedValue || debouncedValue.length === 0)
  ) {
    setPrevDebouncedValue(debouncedValue);
    if (lastLoadedBaseUsername !== null) {
      setLastLoadedBaseUsername(null);
    }
  } else if (debouncedValue !== prevDebouncedValue) {
    setPrevDebouncedValue(debouncedValue);
  }

  const SERVER_COOLDOWN_MS = 30000; // 30 seconds cooldown after server error
  const MAX_CONSECUTIVE_FAILURES = 3; // Stop trying after 3 consecutive failures

  // Debounce value changes
  useEffect(() => {
    if (valueDebounceRef.current) {
      clearTimeout(valueDebounceRef.current);
    }
    valueDebounceRef.current = setTimeout(() => {
      setDebouncedValue(value);
    }, 500);
    return () => {
      if (valueDebounceRef.current) {
        clearTimeout(valueDebounceRef.current);
      }
    };
  }, [value]);

  // Debounce firstName changes
  useEffect(() => {
    if (firstNameDebounceRef.current) {
      clearTimeout(firstNameDebounceRef.current);
    }
    firstNameDebounceRef.current = setTimeout(() => {
      setDebouncedFirstName(firstName || '');
    }, 500);
    return () => {
      if (firstNameDebounceRef.current) {
        clearTimeout(firstNameDebounceRef.current);
      }
    };
  }, [firstName]);

  // Debounce lastName changes
  useEffect(() => {
    if (lastNameDebounceRef.current) {
      clearTimeout(lastNameDebounceRef.current);
    }
    lastNameDebounceRef.current = setTimeout(() => {
      setDebouncedLastName(lastName || '');
    }, 500);
    return () => {
      if (lastNameDebounceRef.current) {
        clearTimeout(lastNameDebounceRef.current);
      }
    };
  }, [lastName]);

  // Debounce workspaceName changes
  const workspaceNameDebounceRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (workspaceNameDebounceRef.current) {
      clearTimeout(workspaceNameDebounceRef.current);
    }
    workspaceNameDebounceRef.current = setTimeout(() => {
      setDebouncedWorkspaceName(workspaceName || '');
    }, 500);
    return () => {
      if (workspaceNameDebounceRef.current) {
        clearTimeout(workspaceNameDebounceRef.current);
      }
    };
  }, [workspaceName]);

  // Helper functions for server error handling
  const handleProposalsServerError = useCallback(() => {
    proposalsConsecutiveFailuresRef.current += 1;
    proposalsLastFailureTimeRef.current = Date.now();

    // If we've had too many consecutive failures, enter cooldown
    if (proposalsConsecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
      setProposalsServerUnavailable(true);

      // Clear any existing cooldown timer
      if (proposalsServerCooldownRef.current) {
        clearTimeout(proposalsServerCooldownRef.current);
      }

      // Set cooldown timer
      proposalsServerCooldownRef.current = setTimeout(() => {
        setProposalsServerUnavailable(false);
        proposalsConsecutiveFailuresRef.current = 0;
        proposalsServerCooldownRef.current = null;
      }, SERVER_COOLDOWN_MS);
    }
  }, []);

  const handleProposalsServerSuccess = useCallback(() => {
    // Reset failure counter on successful request
    proposalsConsecutiveFailuresRef.current = 0;
    proposalsLastFailureTimeRef.current = null;
  }, []);

  const handleAvailabilityServerError = useCallback(() => {
    availabilityConsecutiveFailuresRef.current += 1;
    availabilityLastFailureTimeRef.current = Date.now();

    // If we've had too many consecutive failures, enter cooldown
    if (
      availabilityConsecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES
    ) {
      setAvailabilityServerUnavailable(true);

      // Clear any existing cooldown timer
      if (availabilityServerCooldownRef.current) {
        clearTimeout(availabilityServerCooldownRef.current);
      }

      // Set cooldown timer
      availabilityServerCooldownRef.current = setTimeout(() => {
        setAvailabilityServerUnavailable(false);
        availabilityConsecutiveFailuresRef.current = 0;
        availabilityServerCooldownRef.current = null;
      }, SERVER_COOLDOWN_MS);
    }
  }, []);

  const handleAvailabilityServerSuccess = useCallback(() => {
    // Reset failure counter on successful request
    availabilityConsecutiveFailuresRef.current = 0;
    availabilityLastFailureTimeRef.current = null;
  }, []);

  const filterSuggestionsByAvailability = useCallback(
    async (suggestions: string[], signal: AbortSignal): Promise<string[]> => {
      if (suggestions.length === 0) {
        return [];
      }

      const results = await Promise.all(
        suggestions.map(async (suggestion) => {
          try {
            if (isWorkspaceScope) {
              const response = await WorkspacesApi.checkUsernameAvailability({
                path: { username: suggestion },
                query: excludeWorkspaceId ? { excludeWorkspaceId } : undefined,
                signal,
              });
              return response.data?.available === true ? suggestion : null;
            }

            const response = await UsersPublicApi.checkUsernameAvailability({
              path: { username: suggestion },
              query: excludeUserId ? { excludeUserId } : undefined,
              signal,
            });
            return response.data?.available === true ? suggestion : null;
          } catch {
            // Drop unverified suggestions rather than showing taken ones
            return null;
          }
        }),
      );

      return results.filter((s): s is string => s !== null);
    },
    [excludeUserId, excludeWorkspaceId, isWorkspaceScope],
  );

  // Load proposals based on a base username when the chosen username is unavailable
  const loadProposalsFromBaseUsername = useCallback(
    async (baseUsername: string) => {
      // Don't load if proposals server is unavailable
      if (proposalsServerUnavailable || !baseUsername) {
        return;
      }

      if (lastLoadedBaseUsername === baseUsername) {
        return;
      }

      // Cancel any previous in-flight proposal request
      if (proposalsAbortControllerRef.current) {
        proposalsAbortControllerRef.current.abort();
      }

      const controller = new AbortController();
      proposalsAbortControllerRef.current = controller;

      setIsLoadingProposals(true);
      try {
        const response = isWorkspaceScope
          ? await WorkspacesApi.getUsernameProposals({
              body: {
                baseUsername,
                count: 5,
                excludeWorkspaceId,
              },
              signal: controller.signal,
            })
          : await UsersPublicApi.getUsernameProposals({
              body: {
                baseUsername,
                count: 5,
              },
              signal: controller.signal,
            });

        if (controller.signal.aborted) {
          return;
        }

        handleProposalsServerSuccess();

        const rawSuggestions = response.data.suggestions ?? [];
        const verified = await filterSuggestionsByAvailability(
          rawSuggestions,
          controller.signal,
        );

        if (controller.signal.aborted) {
          return;
        }

        setProposals(verified);
        if (verified.length > 0 && isFocusedRef.current) {
          setShowProposals(true);
        }
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }

        const error = err as AxiosError;
        if (
          !error.response ||
          error.response.status >= 500 ||
          error.code === 'ECONNABORTED' ||
          error.code === 'ERR_NETWORK'
        ) {
          handleProposalsServerError();
        }
        console.error(
          'Failed to load username proposals from base username:',
          err,
        );
      } finally {
        if (!controller.signal.aborted) {
          setIsLoadingProposals(false);
        }
      }
    },
    [
      excludeWorkspaceId,
      filterSuggestionsByAvailability,
      handleProposalsServerError,
      handleProposalsServerSuccess,
      isWorkspaceScope,
      lastLoadedBaseUsername,
      proposalsServerUnavailable,
    ],
  );

  const checkAvailability = useCallback(
    async (username: string) => {
      // Don't check if availability server is unavailable
      if (availabilityServerUnavailable) {
        setAvailabilityStatus('idle');
        return;
      }

      // Cancel previous availability check request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      setAvailabilityStatus('checking');

      try {
        const response = isWorkspaceScope
          ? await WorkspacesApi.checkUsernameAvailability({
              path: { username },
              query: excludeWorkspaceId ? { excludeWorkspaceId } : undefined,
              signal: controller.signal,
            })
          : await UsersPublicApi.checkUsernameAvailability({
              path: { username },
              query: excludeUserId
                ? {
                    excludeUserId,
                  }
                : undefined,
              signal: controller.signal,
            });

        // Check if request was aborted
        if (controller.signal.aborted) {
          return;
        }

        handleAvailabilityServerSuccess();

        // Response data is the actual response body
        if (
          response.data &&
          typeof response.data === 'object' &&
          'available' in response.data
        ) {
          const available = Boolean(
            (response.data as { available?: boolean }).available,
          );
          setAvailabilityStatus(available ? 'available' : 'unavailable');

          // If the username is not available, load suggestions based on this username
          // Only load if this is the current debounced value (prevent stale loads)
          if (!available && debouncedValue === username) {
            if (lastLoadedBaseUsername !== username) {
              setLastLoadedBaseUsername(username);
              void loadProposalsFromBaseUsername(username);
            }
          }
        } else {
          setAvailabilityStatus('idle');
        }
      } catch (err) {
        if (controller.signal.aborted) {
          return; // Request was cancelled
        }

        const error = err as AxiosError;
        if (
          !error.response ||
          error.response.status >= 500 ||
          error.code === 'ECONNABORTED' ||
          error.code === 'ERR_NETWORK' ||
          error.code === 'ERR_CANCELED'
        ) {
          // Server error or network error - enter cooldown
          handleAvailabilityServerError();
          setAvailabilityStatus('idle');
        } else if (error.response?.status === 404) {
          // Username not found means it's available
          handleAvailabilityServerSuccess();
          setAvailabilityStatus('available');
        } else {
          // Other errors - reset to idle
          setAvailabilityStatus('idle');
        }
      }
    },
    [
      availabilityServerUnavailable,
      debouncedValue,
      excludeUserId,
      excludeWorkspaceId,
      handleAvailabilityServerError,
      handleAvailabilityServerSuccess,
      isWorkspaceScope,
      lastLoadedBaseUsername,
      loadProposalsFromBaseUsername,
    ],
  );

  // Check availability when username value changes — always verify via API
  useEffect(() => {
    void (async () => {
      if (!debouncedValue || debouncedValue.length < 3) {
        setAvailabilityStatus('idle');
        return;
      }

      if (lastCheckedUsernameRef.current === debouncedValue) {
        return;
      }

      if (availabilityServerUnavailable) {
        return;
      }

      lastCheckedUsernameRef.current = debouncedValue;
      await checkAvailability(debouncedValue);
    })();
  }, [debouncedValue, availabilityServerUnavailable, checkAvailability]);

  const loadProposals = useCallback(
    async (first: string, last: string, nextWorkspaceName?: string) => {
      // Don't load if proposals server is unavailable
      if (proposalsServerUnavailable) {
        return;
      }

      if (isWorkspaceScope && !nextWorkspaceName?.trim()) {
        return;
      }

      if (!isWorkspaceScope && !first.trim() && !last.trim()) {
        return;
      }

      // Cancel any previous in-flight proposal request
      if (proposalsAbortControllerRef.current) {
        proposalsAbortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      const controller = new AbortController();
      proposalsAbortControllerRef.current = controller;

      setIsLoadingProposals(true);
      try {
        const response = isWorkspaceScope
          ? await WorkspacesApi.getUsernameProposals({
              body: {
                workspaceName: nextWorkspaceName,
                count: 5,
                excludeWorkspaceId,
              },
              signal: controller.signal,
            })
          : await UsersPublicApi.getUsernameProposals({
              body: {
                firstName: first,
                lastName: last,
                count: 5,
              },
              signal: controller.signal,
            });

        // Check if request was aborted
        if (controller.signal.aborted) {
          return;
        }

        handleProposalsServerSuccess();

        const rawSuggestions = response.data.suggestions ?? [];
        const verified = await filterSuggestionsByAvailability(
          rawSuggestions,
          controller.signal,
        );

        if (controller.signal.aborted) {
          return;
        }

        setProposals(verified);
        if (verified.length > 0 && isFocusedRef.current) {
          setShowProposals(true);
        }
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }

        const error = err as AxiosError;
        if (
          !error.response ||
          error.response.status >= 500 ||
          error.code === 'ECONNABORTED' ||
          error.code === 'ERR_NETWORK'
        ) {
          handleProposalsServerError();
        }
        console.error('Failed to load username proposals:', err);
      } finally {
        // Only update loading state if this request wasn't aborted
        if (!controller.signal.aborted) {
          setIsLoadingProposals(false);
        }
      }
    },
    [
      excludeWorkspaceId,
      filterSuggestionsByAvailability,
      handleProposalsServerError,
      handleProposalsServerSuccess,
      isWorkspaceScope,
      proposalsServerUnavailable,
    ],
  );

  // Load proposals when firstName/lastName or workspaceName change
  // Always load proposals when names change - they're just suggestions, not auto-filled
  // Use a ref to track the last loaded names to prevent duplicate requests
  useEffect(() => {
    // For workspaces, use workspaceName; for users, use firstName/lastName
    const hasNameInput =
      debouncedWorkspaceName || debouncedFirstName || debouncedLastName;
    const currentNames = {
      first: debouncedFirstName,
      last: debouncedLastName,
      workspace: debouncedWorkspaceName,
    };

    // Only load if names have actually changed
    const namesChanged =
      !lastLoadedNamesRef.current ||
      lastLoadedNamesRef.current.first !== currentNames.first ||
      lastLoadedNamesRef.current.last !== currentNames.last ||
      lastLoadedNamesRef.current.workspace !== currentNames.workspace;

    // Clear any pending load timeout
    if (proposalsLoadTimeoutRef.current) {
      clearTimeout(proposalsLoadTimeoutRef.current);
      proposalsLoadTimeoutRef.current = null;
    }

    if (hasNameInput && !isLoadingProposals && namesChanged) {
      // Add a small delay to allow browser autofill to complete both fields
      // If we have both names, wait 300ms. If only one, wait 500ms to see if the other will be filled
      // For workspaces, wait 500ms after name change
      const delay = debouncedWorkspaceName
        ? 500
        : debouncedFirstName && debouncedLastName
          ? 300
          : 500;

      proposalsLoadTimeoutRef.current = setTimeout(() => {
        // Check again if names have changed during the delay
        const finalNames = {
          first: debouncedFirstName,
          last: debouncedLastName,
          workspace: debouncedWorkspaceName,
        };

        // Only load if names haven't changed again (to avoid race conditions)
        if (
          finalNames.first === currentNames.first &&
          finalNames.last === currentNames.last &&
          finalNames.workspace === currentNames.workspace
        ) {
          lastLoadedNamesRef.current = finalNames;
          void loadProposals(
            debouncedFirstName,
            debouncedLastName,
            debouncedWorkspaceName,
          );
        }
        proposalsLoadTimeoutRef.current = null;
      }, delay);
    }

    return () => {
      if (proposalsLoadTimeoutRef.current) {
        clearTimeout(proposalsLoadTimeoutRef.current);
        proposalsLoadTimeoutRef.current = null;
      }
    };
  }, [
    debouncedFirstName,
    debouncedLastName,
    debouncedWorkspaceName,
    isLoadingProposals,
    loadProposals,
  ]);

  const handleInputChange = (newValue: string) => {
    // Sanitize input - transliterate umlauts (ä→ae, ß→ss, é→e, …) first so
    // they don't get silently dropped, then keep lowercase alphanumerics plus
    // dot, underscore, hyphen.
    const sanitized = transliterateUsername(newValue)
      .toLowerCase()
      .replace(/[^a-z0-9._-]/g, '');
    onChange(sanitized);

    // Keep proposals visible when user is typing (they might want to select one)
    // Filter proposals to show only matching ones
    if (sanitized.length > 0) {
      // Show proposals if any match what user is typing
      const hasMatchingProposal = proposals.some((p) =>
        p.toLowerCase().includes(sanitized.toLowerCase()),
      );
      if (hasMatchingProposal) {
        setShowProposals(true);
      } else {
        setShowProposals(false);
      }
    } else if (sanitized.length === 0 && proposals.length > 0) {
      // Show all proposals again if they clear the field
      setShowProposals(true);
    }
  };

  const handleProposalClick = (proposal: string) => {
    onChange(proposal);
    setShowProposals(false);
    onProposalSelect?.(proposal);
    // Force a fresh availability check for the selected suggestion
    lastCheckedUsernameRef.current = null;
    setAvailabilityStatus('checking');
  };

  const getStatusIcon = () => {
    // Show loading spinner while proposals are being fetched, regardless of
    // whether the user has started typing in the username field yet.
    if (isLoadingProposals && resolvedAvailabilityStatus !== 'unavailable') {
      return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
    }

    // Don't show icon if server is unavailable
    if (availabilityServerUnavailable) {
      return null;
    }

    switch (resolvedAvailabilityStatus) {
      case 'checking':
        return (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        );
      case 'available':
        return <CheckCircle2 className={`h-4 w-4 ${healthOkFgClass}`} />;
      case 'unavailable':
        return <XCircle className="h-4 w-4 text-destructive" />;
      default:
        return null;
    }
  };

  // Filter proposals based on current input value
  const filteredProposals = proposals.filter(
    (proposal) =>
      !value || proposal.toLowerCase().includes(value.toLowerCase()),
  );

  return (
    <div className="space-y-2">
      <div className="relative">
        <Input
          id={id}
          type="text"
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => {
            isFocusedRef.current = true;
            if (filteredProposals.length > 0) {
              setShowProposals(true);
            }
          }}
          onBlur={(e) => {
            isFocusedRef.current = false;
            // Don't hide if clicking on a proposal
            // Use a small delay to allow click event to fire first
            setTimeout(() => {
              const relatedTarget = e.relatedTarget as HTMLElement;
              if (
                !relatedTarget ||
                !relatedTarget.closest('.proposals-dropdown')
              ) {
                setShowProposals(false);
              }
            }, 200);
          }}
          disabled={disabled}
          className="pr-10"
          placeholder={placeholder ?? authDict.usernamePlaceholder}
        />
        {/* Show icon for checking, available, or unavailable states.
            Also show while proposals are loading so the user sees feedback
            when switching focus from the workspace name field. */}
        {(value || isLoadingProposals) && getStatusIcon() && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {getStatusIcon()}
          </div>
        )}

        {/* Proposals dropdown - absolutely positioned to overlay */}
        <AnimatePresence>
          {showProposals && filteredProposals.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="proposals-dropdown absolute left-0 top-full z-50 mt-1 w-full rounded-lg border bg-card p-2 shadow-md"
            >
              <p className="mb-2 text-xs font-medium text-muted-foreground">
                {authDict.usernameSuggestions}
              </p>
              <div className="space-y-1">
                {filteredProposals.map((proposal) => (
                  <button
                    key={proposal}
                    type="button"
                    onClick={() => handleProposalClick(proposal)}
                    className="w-full rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none"
                  >
                    {proposal}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Error message */}
      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Only show text message when username is unavailable */}
      {value &&
        !availabilityServerUnavailable &&
        resolvedAvailabilityStatus === 'unavailable' && (
          <p className="text-xs text-destructive">
            {authDict.usernameUnavailable}
          </p>
        )}
    </div>
  );
}
